import React, { useEffect, useMemo, useState } from "react";
import { Share2, Check } from "lucide-react";
import { Wrap, SectionHead, Note, Reveal } from "./Section";
import { useToast } from "../hooks/use-toast";
import api from "../lib/api";

const CAP_PRESETS = [1000, 5000, 10000, 25000];
const RET_PRESETS = [0.5, 0.75, 1];
const HORIZON_PRESETS = [1, 30, 60];

function fmt(n) {
  return "$" + Math.round(n).toLocaleString();
}

export default function Calculator() {
  const [capital, setCapital] = useState(1000);
  const [mult, setMult] = useState(12);
  const [ret, setRet] = useState(1);
  const [months, setMonths] = useState(12);
  const [addDep, setAddDep] = useState(0);
  const [addDepFreq, setAddDepFreq] = useState(1);
  const [withdraw, setWithdraw] = useState(0);
  const [withdrawFreq, setWithdrawFreq] = useState(1);
  const [compound, setCompound] = useState(true);
  const [showFlows, setShowFlows] = useState(false);
  const [shareId, setShareId] = useState(null);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  // Load shared scenario from ?s=id
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("s");
    if (!id) return;
    api.get(`/scenarios/${id}`).then((r) => {
      const c = r.data?.config || {};
      if (c.capital != null) setCapital(c.capital);
      if (c.mult != null) setMult(c.mult);
      if (c.ret != null) setRet(c.ret);
      if (c.months != null) setMonths(c.months);
      if (c.addDep != null) setAddDep(c.addDep);
      if (c.addDepFreq != null) setAddDepFreq(c.addDepFreq);
      if (c.withdraw != null) setWithdraw(c.withdraw);
      if (c.withdrawFreq != null) setWithdrawFreq(c.withdrawFreq);
      if (c.compound != null) setCompound(c.compound);
      if (c.showFlows != null) setShowFlows(c.showFlows);
      setShareId(id);
      setTimeout(() => document.querySelector("#calculator")?.scrollIntoView(), 300);
    }).catch(() => {});
  }, []);

  const saveScenario = async () => {
    setSaving(true);
    try {
      const config = { capital, mult, ret, months, addDep, addDepFreq, withdraw, withdrawFreq, compound, showFlows };
      const r = await api.post("/scenarios", { config });
      const id = r.data.id;
      setShareId(id);
      const url = `${window.location.origin}${window.location.pathname}?s=${id}`;
      window.history.replaceState(null, "", `?s=${id}`);
      try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2500); } catch (e) {}
      toast({ title: "Scenario saved", description: "Shareable link copied to your clipboard." });
    } catch (e) {
      toast({ title: "Could not save", description: "Please try again in a moment." });
    }
    setSaving(false);
  };

  const ddLimit = mult === 12 ? 0.1 : 0.05;

  const result = useMemo(() => {
    let value = capital;
    let contributed = capital;
    let peak = capital;
    let highWater = capital;
    let totalWithdrawn = 0;
    let closed = false;
    let closedMonth = null;
    const rows = [];

    for (let m = 1; m <= months; m++) {
      if (closed) break;
      const base = compound ? value : contributed;
      const volume = base * mult;
      const gross = volume * (ret / 100);
      let fee = 0;
      const beforeFee = value + gross;
      if (gross > 0) {
        const taxable = Math.max(0, beforeFee - highWater);
        fee = taxable * 0.3;
      }
      const net = gross - fee;
      value += net;
      if (value > highWater) highWater = value;
      if (value > peak) peak = value;

      // flows
      let dep = 0, wd = 0;
      if (addDep > 0 && addDepFreq > 0 && m % addDepFreq === 0) {
        dep = addDep; value += dep; contributed += dep;
      }
      if (withdraw > 0 && withdrawFreq > 0 && m % withdrawFreq === 0) {
        wd = Math.min(withdraw, value); value -= wd; totalWithdrawn += wd;
      }

      // drawdown check (account level)
      const dd = peak > 0 ? (peak - value) / peak : 0;
      if (dd >= ddLimit) {
        closed = true; closedMonth = m;
      }

      rows.push({ m, volume, gross, fee, net, value, dep, wd });
    }

    const net = value - contributed + totalWithdrawn;
    const gainPct = contributed > 0 ? (net / capital) * 100 : 0;
    return {
      rows, value, contributed, totalWithdrawn, closed, closedMonth,
      net, gainPct, multipleVsCap: capital > 0 ? value / capital : 0,
      grossTotal: rows.reduce((a, r) => a + r.gross, 0),
      feeTotal: rows.reduce((a, r) => a + r.fee, 0),
      firstVolume: rows[0]?.volume || capital * mult,
    };
  }, [capital, mult, ret, months, addDep, addDepFreq, withdraw, withdrawFreq, compound, ddLimit]);

  // chart
  const chart = useMemo(() => {
    const w = 800, h = 200;
    const vals = [capital, ...result.rows.map((r) => r.value)];
    const max = Math.max(...vals, 1);
    const min = Math.min(...vals, 0);
    const pts = vals.map((v, i) => {
      const x = (i / (vals.length - 1 || 1)) * w;
      const y = h - ((v - min) / (max - min || 1)) * (h - 10) - 5;
      return [x, y];
    });
    const line = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
    return { line, area: `${line} L${w},${h} L0,${h} Z`, max, min };
  }, [result, capital]);

  const Toggle = ({ on, set, children, sub }) => (
    <button onClick={() => set(!on)} className="flex items-start gap-3 text-left">
      <span className={`mt-0.5 w-9 h-5 rounded-full flex items-center transition-colors shrink-0 ${on ? "bg-accent" : "bg-white/10"}`}>
        <span className={`w-4 h-4 rounded-full bg-[#0a0d08] transition-transform mx-0.5 ${on ? "translate-x-4" : ""}`} />
      </span>
      <span>
        <span className="text-[13px] text-[var(--text)] font-medium">{children}</span>
        <span className="block text-[11px] text-[var(--muted)] leading-snug mt-0.5">{sub}</span>
      </span>
    </button>
  );

  const Chip = ({ active, onClick, children }) => (
    <button onClick={onClick}
      className={`px-3 py-1.5 rounded-lg mono text-[11px] transition-colors ${active ? "bg-accent text-[#0a0d08]" : "bg-white/[0.03] text-[var(--muted)] hover:text-[var(--text)] border border-white/[0.07]"}`}>
      {children}
    </button>
  );

  return (
    <Wrap id="calculator">
      <SectionHead
        tag="Illustrative scenario builder"
        title="Run the numbers"
        highlight="yourself"
        desc="Change any assumption — including a negative month — and watch the arithmetic follow. This tool applies the published mechanics: volume equals your capital times the multiplier, the 30% performance fee is charged on a high-water-mark basis, and the account closes if its drawdown limit is breached. It is not a forecast of Sonic AI performance."
        link={
          <button onClick={saveScenario} disabled={saving}
            className="mt-5 inline-flex items-center gap-2 btn-ghost px-4 py-2.5 rounded-lg disabled:opacity-60">
            {copied ? <Check size={13} /> : <Share2 size={13} />}
            {copied ? "Link copied" : saving ? "Saving…" : "Save & share this scenario"}
          </button>
        }
      />

      <div className="mt-12 grid lg:grid-cols-2 gap-5">
        {/* Assumptions */}
        <Reveal direction="left" className="h-full">
        <div className="card rounded-2xl p-7 h-full">
          <div className="label text-accent mb-6">Assumptions</div>

          <div className="mb-6">
            <div className="label text-[var(--muted)] text-[0.6rem] mb-2">Starting capital</div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[var(--muted)] mono">$</span>
              <input type="number" value={capital} onChange={(e) => setCapital(Math.max(0, +e.target.value))}
                className="bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 w-full text-[var(--text)] mono focus:border-accent/50 outline-none" />
            </div>
            <div className="flex gap-2">
              {CAP_PRESETS.map((c) => <Chip key={c} active={capital === c} onClick={() => setCapital(c)}>{c / 1000}k</Chip>)}
            </div>
          </div>

          <div className="mb-6">
            <div className="label text-[var(--muted)] text-[0.6rem] mb-2">Account structure</div>
            <div className="flex gap-2">
              <Chip active={mult === 12} onClick={() => setMult(12)}>12×</Chip>
              <Chip active={mult === 24} onClick={() => setMult(24)}>24×</Chip>
              <span className="text-[11px] text-[var(--muted)] self-center ml-2">Drawdown limit {ddLimit * 100}%</span>
            </div>
          </div>

          <div className="mb-6">
            <div className="label text-[var(--muted)] text-[0.6rem] mb-2">Monthly return on trading volume</div>
            <div className="flex items-center gap-2 mb-2">
              <input type="number" step="0.25" value={ret} onChange={(e) => setRet(+e.target.value)}
                className="bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 w-full text-[var(--text)] mono focus:border-accent/50 outline-none" />
              <span className="text-[var(--muted)] mono">%</span>
            </div>
            <div className="flex gap-2">
              {RET_PRESETS.map((r) => <Chip key={r} active={ret === r} onClick={() => setRet(r)}>{r}%</Chip>)}
            </div>
            <p className="text-[11px] text-[var(--muted)] mt-2">The strategy's stated target range is 0.5–1% on volume. Enter a negative figure to model a losing run.</p>
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <div className="label text-[var(--muted)] text-[0.6rem]">Time horizon</div>
              <span className="mono text-accent text-sm">{months} months</span>
            </div>
            <input type="range" min="1" max="60" value={months} onChange={(e) => setMonths(+e.target.value)} className="range-accent w-full" />
            <div className="flex gap-2 mt-2">
              {HORIZON_PRESETS.map((h) => <Chip key={h} active={months === h} onClick={() => setMonths(h)}>{h}</Chip>)}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <div className="label text-[var(--muted)] text-[0.6rem] mb-2">Additional deposit</div>
              <div className="flex items-center gap-1">
                <span className="text-[var(--muted)] mono text-sm">$</span>
                <input type="number" value={addDep} onChange={(e) => setAddDep(Math.max(0, +e.target.value))}
                  className="bg-white/[0.03] border border-white/[0.08] rounded-lg px-2 py-2 w-full text-[var(--text)] mono text-sm focus:border-accent/50 outline-none" />
                <span className="text-[var(--muted)] mono text-[10px]">/{addDepFreq}mo</span>
              </div>
            </div>
            <div>
              <div className="label text-[var(--muted)] text-[0.6rem] mb-2">Withdrawal</div>
              <div className="flex items-center gap-1">
                <span className="text-[var(--muted)] mono text-sm">$</span>
                <input type="number" value={withdraw} onChange={(e) => setWithdraw(Math.max(0, +e.target.value))}
                  className="bg-white/[0.03] border border-white/[0.08] rounded-lg px-2 py-2 w-full text-[var(--text)] mono text-sm focus:border-accent/50 outline-none" />
                <span className="text-[var(--muted)] mono text-[10px]">/{withdrawFreq}mo</span>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <Toggle on={compound} set={setCompound} sub="On: trading volume grows with the account. Off: volume stays on capital you paid in.">Compound net profit</Toggle>
            <Toggle on={showFlows} set={setShowFlows} sub="Adds the contributed-capital line to the flow columns of the table.">Show deposits &amp; withdrawals</Toggle>
          </div>
        </div>
        </Reveal>

        {/* Results */}
        <Reveal direction="right" delay={1} className="h-full">
        <div className="card rounded-2xl p-7 h-full">
          {result.closed && (
            <div className="mb-5 border border-orange-400/40 bg-orange-400/5 rounded-lg px-4 py-3">
              <span className="text-[12px] text-orange-400">Account drawdown limit {ddLimit * 100}% — breached in month {result.closedMonth}, the scenario closes the account.</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-x-6 gap-y-5 mb-6">
            {[
              ["Starting capital", fmt(capital)],
              [`Trading volume, month 1 (${mult}×)`, fmt(result.firstVolume)],
              ["Gross illustrative profit", (result.grossTotal >= 0 ? "+" : "") + fmt(result.grossTotal)],
              ["Illustrative performance fee (30%)", "−" + fmt(result.feeTotal)],
              ["Total withdrawals taken", fmt(result.totalWithdrawn)],
              ["Total capital contributed", fmt(result.contributed)],
            ].map((r, i) => (
              <div key={i}>
                <div className="label text-[var(--muted)] text-[0.56rem] mb-1.5 leading-tight">{r[0]}</div>
                <div className={`mono text-lg ${i === 2 ? "text-accent" : i === 3 ? "text-orange-400" : "text-[var(--text)]"}`}>{r[1]}</div>
              </div>
            ))}
          </div>

          <div className="border-t border-white/[0.07] pt-5 mb-5">
            <div className="flex items-end justify-between">
              <div>
                <div className="label text-[var(--muted)] text-[0.56rem] mb-1.5">Projected account value, month {result.rows.length}</div>
                <div className="display text-accent text-3xl">{fmt(result.value)}</div>
              </div>
              <div className="text-right">
                <div className="label text-[var(--muted)] text-[0.56rem] mb-1.5">Net gain / loss</div>
                <div className={`mono text-lg ${result.net >= 0 ? "text-accent" : "text-orange-400"}`}>
                  {(result.net >= 0 ? "+" : "−") + fmt(Math.abs(result.net))} ({result.gainPct >= 0 ? "+" : ""}{result.gainPct.toFixed(1)}%)
                </div>
                <div className="text-[11px] text-[var(--muted)] mt-1">Multiple vs contributed {result.multipleVsCap.toFixed(2)}×</div>
              </div>
            </div>
          </div>

          <svg viewBox="0 0 800 200" className="w-full h-auto" preserveAspectRatio="none">
            <defs>
              <linearGradient id="calc" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgba(196,245,66,0.25)" />
                <stop offset="100%" stopColor="rgba(196,245,66,0)" />
              </linearGradient>
            </defs>
            <path d={chart.area} fill="url(#calc)" />
            <path d={chart.line} fill="none" stroke="#c4f542" strokeWidth="2.5" strokeLinejoin="round" />
          </svg>
        </div>
        </Reveal>
      </div>

      {/* Month by month table */}
      <Reveal delay={1}>
      <div className="mt-6 card rounded-2xl p-6 overflow-x-auto">
        <div className="label text-[var(--muted)] mb-4">Month by month</div>
        <table className="w-full text-[13px] mono min-w-[640px]">
          <thead>
            <tr className="text-[var(--muted)] label text-[0.56rem]">
              <th className="text-left py-2 font-normal">Mo</th>
              <th className="text-right py-2 font-normal">Volume</th>
              <th className="text-right py-2 font-normal">Gross</th>
              <th className="text-right py-2 font-normal">Fee</th>
              <th className="text-right py-2 font-normal">Net</th>
              {showFlows && <th className="text-right py-2 font-normal">Dep/WD</th>}
              <th className="text-right py-2 font-normal">Account value</th>
            </tr>
          </thead>
          <tbody>
            {result.rows.map((r) => (
              <tr key={r.m} className="border-t border-white/[0.05]">
                <td className="py-2 text-[var(--muted)]">{r.m}</td>
                <td className="py-2 text-right text-[var(--text)]">{fmt(r.volume)}</td>
                <td className="py-2 text-right text-accent">{fmt(r.gross)}</td>
                <td className="py-2 text-right text-orange-400">−{fmt(r.fee)}</td>
                <td className="py-2 text-right text-[var(--text)]">{fmt(r.net)}</td>
                {showFlows && <td className="py-2 text-right text-[var(--muted)]">{r.dep ? "+" + fmt(r.dep) : ""}{r.wd ? " −" + fmt(r.wd) : ""}{!r.dep && !r.wd ? "—" : ""}</td>}
                <td className="py-2 text-right text-[var(--text)]">{fmt(r.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </Reveal>

      <Note>
        Every figure here is an illustration of the account mechanics at the assumption you entered — not a projection, forecast or promise of Sonic AI performance. It ignores swap, spread, slippage, broker fees, taxes and the timing of individual trades. Real months vary, some are negative, and the deposited capital can be lost in full.
      </Note>
    </Wrap>
  );
}
